import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import { FirestoreService } from '../../../../sdk/firestoreService';

/**
 * PublicScreen Actions
 * כל הלוגיקה של טעינת מסך ציבורי והאזנה לשינויים
 */

// ============================================
// Helper Functions
// ============================================

/**
 * מוצא את הפלייליסט הפעיל לפי השעה והיום הנוכחיים
 * @param {Object|Array} schedule - אובייקט/מערך ה-schedule של המסך
 * @returns {Object|null} - הפריט הפעיל עם timeUntilEnd או null
 */
const getActiveScheduleItem = (schedule) => {
    const now = new Date();
    const currentDay = now.getDay(); // 0=ראשון, 6=שבת
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`;

    console.log('🕐 זמן נוכחי:', currentTime, 'יום:', currentDay, `(${['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'][currentDay]})`);
    console.log('📋 Schedule שהתקבל:', JSON.stringify(schedule, null, 2));

    // תמיכה במבנה החדש (אובייקט) והישן (מערך)
    if (Array.isArray(schedule)) {
        // מבנה ישן: schedule הוא מערך
        for (const item of schedule) {
            if (!item.days || !item.days.includes(currentDay)) {
                continue;
            }

            if (currentTime >= item.startTime && currentTime <= item.endTime) {
                const [endHour, endMinute] = item.endTime.split(':').map(Number);
                const endDate = new Date();
                endDate.setHours(endHour, endMinute, 0, 0);

                const timeUntilEnd = endDate - now;

                console.log('✅ פלייליסט פעיל נמצא (מבנה ישן):', item.playlistId);
                console.log('⏱️ זמן עד סוף הטווח:', Math.floor(timeUntilEnd / 1000 / 60), 'דקות');

                return {
                    ...item,
                    timeUntilEnd
                };
            }
        }
    } else {
        // מבנה חדש: schedule הוא אובייקט
        const todaySchedule = schedule[currentDay];

        if (!todaySchedule || todaySchedule.length === 0) {
            console.log('⚠️ אין לוח זמנים ליום זה');
            return null;
        }

        for (const item of todaySchedule) {
            // המרת זמנים להשוואה
            const [startHour, startMinute] = item.startTime.split(':').map(Number);
            const [endHour, endMinute] = item.endTime.split(':').map(Number);

            const startTimeMinutes = startHour * 60 + startMinute;
            const endTimeMinutes = endHour * 60 + endMinute;
            const currentTimeMinutes = currentHour * 60 + currentMinute;

            // טיפול במקרה של 23:59 (סוף היום)
            const actualEndMinutes = (endHour === 23 && endMinute === 59) ? 24 * 60 : endTimeMinutes;

            if (currentTimeMinutes >= startTimeMinutes && currentTimeMinutes < actualEndMinutes) {
                // חישוב זמן עד סוף המקטע
                const endDate = new Date();
                if (endHour === 23 && endMinute === 59) {
                    // אם זה 23:59, מגדירים ל-00:00 של מחר
                    endDate.setDate(endDate.getDate() + 1);
                    endDate.setHours(0, 0, 0, 0);
                } else {
                    endDate.setHours(endHour, endMinute, 0, 0);
                }

                const timeUntilEnd = endDate - now;

                console.log('✅ פלייליסט פעיל נמצא:', item.playlistId);
                console.log('📅 מקטע:', `${item.startTime} - ${item.endTime}`);
                console.log('⏱️ זמן עד סוף המקטע:', Math.floor(timeUntilEnd / 1000 / 60), 'דקות');

                return {
                    ...item,
                    timeUntilEnd,
                    currentDay
                };
            }
        }
    }

    console.log('⚠️ לא נמצא פלייליסט פעיל לשעה זו');
    return null;
};

/**
 * מחשב את הזמן עד חצות (00:00)
 * @returns {number} - מילישניות עד חצות
 */
/**
 * מחשב את הזמן עד הפלייליסט הבא
 * @param {Object} schedule - אובייקט ה-schedule של המסך
 * @returns {number|null} - מילישניות עד הפלייליסט הבא או null
 */
export const getTimeUntilNextPlaylist = (schedule) => {
    const now = new Date();
    const currentDay = now.getDay();
    const currentTime = now.getHours() * 60 + now.getMinutes(); // דקות מתחילת היום

    // בודק אם יש פלייליסט נוסף היום
    const todaySchedule = schedule[currentDay] || [];
    for (const item of todaySchedule) {
        const [startHour, startMinute] = item.startTime.split(':').map(Number);
        const startTimeInMinutes = startHour * 60 + startMinute;

        if (startTimeInMinutes > currentTime) {
            const timeUntil = (startTimeInMinutes - currentTime) * 60 * 1000;
            console.log('⏭️ פלייליסט הבא היום בעוד:', Math.floor(timeUntil / 1000 / 60), 'דקות');
            return timeUntil;
        }
    }

    // בודק ימים הבאים
    for (let daysAhead = 1; daysAhead <= 7; daysAhead++) {
        const futureDay = (currentDay + daysAhead) % 7;
        const futureDaySchedule = schedule[futureDay] || [];

        if (futureDaySchedule.length > 0) {
            const firstItem = futureDaySchedule[0];
            const [startHour, startMinute] = firstItem.startTime.split(':').map(Number);

            const minutesUntilMidnight = (24 * 60) - currentTime;
            const minutesInBetweenDays = (daysAhead - 1) * 24 * 60;
            const startTimeInMinutes = startHour * 60 + startMinute;

            const timeUntil = (minutesUntilMidnight + minutesInBetweenDays + startTimeInMinutes) * 60 * 1000;
            console.log(`⏭️ פלייליסט הבא ביום ${['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'][futureDay]} בעוד:`, Math.floor(timeUntil / 1000 / 60 / 60), 'שעות');
            return timeUntil;
        }
    }

    return null;
};

// ============================================
// Action Creators
// ============================================

// Load Public Screen
export const loadPublicScreenStart = () => ({
    type: 'LOAD_PUBLIC_SCREEN_START'
});

export const loadPublicScreenSuccess = (organization, playlist, dashboard) => ({
    type: 'LOAD_PUBLIC_SCREEN_SUCCESS',
    payload: { organization, playlist, dashboard }
});

export const loadPublicScreenFailure = (error) => ({
    type: 'LOAD_PUBLIC_SCREEN_FAILURE',
    payload: error
});

// Load Dashboard
export const loadDashboardStart = () => ({
    type: 'LOAD_DASHBOARD_START'
});

export const loadDashboardSuccess = (dashboard) => ({
    type: 'LOAD_DASHBOARD_SUCCESS',
    payload: dashboard
});

export const loadDashboardFailure = (error) => ({
    type: 'LOAD_DASHBOARD_FAILURE',
    payload: error
});

// Load All Dashboards
export const loadAllDashboardsStart = () => ({
    type: 'LOAD_ALL_DASHBOARDS_START'
});

export const loadAllDashboardsSuccess = (dashboards) => ({
    type: 'LOAD_ALL_DASHBOARDS_SUCCESS',
    payload: dashboards
});

export const loadAllDashboardsFailure = (error) => ({
    type: 'LOAD_ALL_DASHBOARDS_FAILURE',
    payload: error
});

// Playlist Listener
export const playlistUpdated = (playlist) => ({
    type: 'PLAYLIST_UPDATED',
    payload: playlist
});

export const playlistListenerError = (error) => ({
    type: 'PLAYLIST_LISTENER_ERROR',
    payload: error
});

// ============================================
// Thunk Actions
// ============================================

/**
 * טעינת מסך ציבורי לפי orgHash ו-screenName
 */
export const loadPublicScreen = (orgHash, screenName) => async (dispatch) => {
    dispatch(loadPublicScreenStart());
    try {
        console.log('🔍 מחפש מסך:', { orgHash, screenName });

        // 1. מציאת הארגון לפי hash
        const organization = await FirestoreService.getOrganizationByHash(orgHash);
        if (!organization) {
            throw new Error('ארגון לא נמצא');
        }
        console.log('✅ ארגון נמצא:', organization.organizationId);

        // 2. מציאת המסך לפי URL
        const screens = await FirestoreService.getScreensByOrganization(organization.organizationId);
        const screen = screens.find(s => s.url === screenName);

        if (!screen) {
            throw new Error('מסך לא נמצא');
        }
        console.log('✅ מסך נמצא:', screen.screenId);

        // 3. קבלת הפלייליסט הנכון לפי ה-schedule (שעה ויום)
        if (!screen.schedule || (Array.isArray(screen.schedule) ? screen.schedule.length === 0 : Object.keys(screen.schedule).length === 0)) {
            throw new Error('אין לוח זמנים למסך זה');
        }

        const activeScheduleItem = getActiveScheduleItem(screen.schedule);
        if (!activeScheduleItem) {
            throw new Error('אין פלייליסט פעיל בשעה זו');
        }

        const playlist = await FirestoreService.getPlaylist(activeScheduleItem.playlistId);

        if (!playlist || !playlist.items || playlist.items.length === 0) {
            throw new Error('הפלייליסט ריק');
        }

        console.log('✅ פלייליסט נטען:', playlist.playlistId, 'עם', playlist.items.length, 'דשבורדים');

        // 4. טעינת הדשבורד הראשון שפעיל ופורסם
        let dashboard = null;
        for (const item of playlist.items) {
            // דלג על לוחות מושבתים
            if (item.enabled === false) {
                console.log('⏭️ דשבורד מושבת, מדלג:', item.dashboardId);
                continue;
            }
            const tempDashboard = await FirestoreService.getDashboard(item.dashboardId);
            // בודק אם הדשבורד פורסם (יש לו לפחות גירסה אחת)
            if (tempDashboard && tempDashboard.versions && tempDashboard.versions.length > 0) {
                dashboard = tempDashboard;
                console.log('✅ דשבורד ראשון שפעיל ופורסם נטען:', dashboard.dashboardId);
                break;
            } else {
                console.log('⚠️ דשבורד לא פורסם עדיין, מדלג:', tempDashboard?.dashboardId);
            }
        }

        if (!dashboard) {
            throw new Error('אין לוח פורסמים בפלייליסט זה');
        }

        dispatch(loadPublicScreenSuccess(organization, playlist, dashboard));

        return {
            organization,
            screen,
            playlist,
            dashboard,
            activeScheduleItem
        };
    } catch (error) {
        console.error('❌ שגיאה בטעינת המסך:', error);
        dispatch(loadPublicScreenFailure(error.message));
        throw error;
    }
};

/**
 * טעינת דשבורד לפי ID
 */
export const loadDashboard = (dashboardId) => async (dispatch) => {
    dispatch(loadDashboardStart());
    try {
        const dashboard = await FirestoreService.getDashboard(dashboardId);
        dispatch(loadDashboardSuccess(dashboard));
        return dashboard;
    } catch (error) {
        console.error('❌ שגיאה בטעינת הדשבורד:', error);
        dispatch(loadDashboardFailure(error.message));
        throw error;
    }
};

/**
 * טעינת כל הדשבורדים של הפלייליסט מראש
 * @param {Array} playlistItems - רשימת הפריטים בפלייליסט
 */
export const loadAllDashboards = (playlistItems) => async (dispatch) => {
    dispatch(loadAllDashboardsStart());
    try {
        console.log('📦 טוען את כל הדשבורדים מראש...');

        // טעינת כל הדשבורדים במקביל
        const dashboardPromises = playlistItems.map(item =>
            FirestoreService.getDashboard(item.dashboardId)
        );

        const dashboards = await Promise.all(dashboardPromises);

        // יצירת מפה של dashboardId -> dashboard
        // מסנן רק דשבורדים שיש להם לפחות גירסה אחת (פורסמו)
        const dashboardsMap = {};
        dashboards.forEach(dashboard => {
            if (dashboard && dashboard.versions && dashboard.versions.length > 0) {
                dashboardsMap[dashboard.dashboardId] = dashboard;
            } else {
                console.log('⚠️ דשבורד לא פורסם עדיין, מדלג:', dashboard?.dashboardId);
            }
        });

        console.log('✅ כל הדשבורדים נטענו:', Object.keys(dashboardsMap).length);
        dispatch(loadAllDashboardsSuccess(dashboardsMap));

        return dashboardsMap;
    } catch (error) {
        console.error('❌ שגיאה בטעינת הדשבורדים:', error);
        dispatch(loadAllDashboardsFailure(error.message));
        throw error;
    }
};

/**
 * יצירת Realtime Listener למסך (Screen) - מאזין לשינויים ב-schedule
 * @param {string} screenId - מזהה המסך
 * @param {Function} onScheduleChange - callback שמופעל כשה-schedule משתנה
 * @returns {Function} unsubscribe function לניתוק ה-listener
 */
export const createScreenListener = (screenId, onScheduleChange) => {
    console.log('🔥 מתחיל להאזין לשינויים במסך:', screenId);

    const db = getFirestore();
    const screenRef = doc(db, 'screens', screenId);

    let isFirstSnapshot = true; // דגל למעקב אחרי הסנפשוט הראשון

    const unsubscribe = onSnapshot(
        screenRef,
        (docSnapshot) => {
            if (docSnapshot.exists()) {
                // מתעלם מהסנפשוט הראשון (זה המסך שכבר טענו)
                if (isFirstSnapshot) {
                    console.log('📸 Snapshot ראשון - מתעלם (זה המסך שכבר טענו)');
                    isFirstSnapshot = false;
                    return;
                }

                const updatedScreen = {
                    screenId: docSnapshot.id,
                    ...docSnapshot.data()
                };

                console.log('🔄 מסך התעדכן - schedule חדש זוהה!');
                onScheduleChange(updatedScreen.schedule);
            } else {
                console.log('⚠️ המסך נמחק');
            }
        },
        (error) => {
            console.error('❌ שגיאה ב-listener של המסך:', error);
        }
    );

    return unsubscribe;
};

/**
 * יצירת Realtime Listener לדשבורד
 * @param {string} dashboardId - מזהה הדשבורד
 * @param {Function} dispatch - Redux dispatch function
 * @returns {Function} unsubscribe function לניתוק ה-listener
 */
export const createDashboardListener = (dashboardId, dispatch) => {
    console.log('🔥 מתחיל להאזין לשינויים בדשבורד:', dashboardId);

    const db = getFirestore();
    const dashboardRef = doc(db, 'dashboards', dashboardId);

    let lastVersionId = null; // שמור את ה-versionId האחרון

    const unsubscribe = onSnapshot(
        dashboardRef,
        async (docSnapshot) => {
            if (docSnapshot.exists()) {
                const data = docSnapshot.data();

                // Support both new (latestVersionId field) and legacy (versions array) formats
                const currentVersionId = data.latestVersionId
                    || data.versions?.[data.versions.length - 1]?.versionId;

                if (!currentVersionId) return;

                // אם זו הפעם הראשונה, שמור את ה-versionId
                if (lastVersionId === null) {
                    console.log('📸 Dashboard Snapshot ראשון - שומר versionId:', currentVersionId);
                    lastVersionId = currentVersionId;
                    return;
                }

                // עדכן רק אם יש גירסה חדשה
                if (currentVersionId !== lastVersionId) {
                    console.log('🔄 דשבורד התעדכן עם גירסה חדשה:', dashboardId, '(', lastVersionId, '→', currentVersionId, ')');
                    lastVersionId = currentVersionId;

                    // Fetch full dashboard including versions from subcollection
                    const updatedDashboard = await FirestoreService.getDashboard(docSnapshot.id);
                    if (updatedDashboard) {
                        dispatch(loadDashboardSuccess(updatedDashboard));
                    }
                }
            } else {
                console.log('⚠️ הדשבורד נמחק');
            }
        },
        (error) => {
            console.error('❌ שגיאה ב-listener של הדשבורד:', error);
        }
    );

    return unsubscribe;
};

/**
 * יצירת Realtime Listener לפלייליסט
 * @param {string} playlistId - מזהה הפלייליסט
 * @param {Function} dispatch - Redux dispatch function
 * @returns {Function} unsubscribe function לניתוק ה-listener
 */
export const createPlaylistListener = (playlistId, dispatch) => {
    console.log('🔥 מתחיל להאזין לשינויים בפלייליסט:', playlistId);

    const db = getFirestore();
    const playlistRef = doc(db, 'playlists', playlistId);

    const unsubscribe = onSnapshot(
        playlistRef,
        (docSnapshot) => {
            if (docSnapshot.exists()) {
                const updatedPlaylist = {
                    playlistId: docSnapshot.id,
                    ...docSnapshot.data()
                };

                console.log('🔄 פלייליסט התעדכן:', updatedPlaylist);
                dispatch(playlistUpdated(updatedPlaylist));
            } else {
                console.log('⚠️ הפלייליסט נמחק');
                dispatch(playlistListenerError('הפלייליסט נמחק'));
            }
        },
        (error) => {
            console.error('❌ שגיאה ב-listener:', error);
            dispatch(playlistListenerError(error.message));
        }
    );

    return unsubscribe;
};
